import { reaction_like, reaction_celebrate, reaction_love, reaction_insightful, reaction_thinking, reaction_support } from '@assets/svg';
import { BrowserCustomerClient } from '@client';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ResourceCommentReactionsViewModel } from '@models/api';
import { classNames } from '@utilities';
import React, { useEffect, useState, Fragment } from 'react';
import { ReactionPopupProps } from '../video-item-props';

interface Reaction {
  icon: string;
  count: number;
}

interface ReactionsMapData {
  name: string;
  icon: string;
}

const reactionMapData: ReactionsMapData[] = [
  {
    name: 'reaction_like',
    icon: reaction_like,
  },
  {
    name: 'reaction_celebrate',
    icon: reaction_celebrate,
  },
  {
    name: 'reaction_love',
    icon: reaction_love,
  },
  {
    name: 'reaction_insightful',
    icon: reaction_insightful,
  },
  {
    name: 'reaction_thinking',
    icon: reaction_thinking,
  },
  {
    name: 'reaction_support',
    icon: reaction_support,
  },
];

const ReactionPopUp: React.FC<ReactionPopupProps> = ({ commentId, open, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [reactions, setReactions] = useState<ResourceCommentReactionsViewModel[]>([]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      const client = new BrowserCustomerClient();
      const response = await client?.GetResourceCommentReactions(commentId);
      if (response?.success) {
        const respJson = response.data;
        setReactions(respJson);
      }
    })();
  }, [commentId, open]);

  const likeReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'like';
  });

  const celebrateReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'celebrate';
  });

  const loveReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'love';
  });

  const insightfulReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'insightful';
  });

  const thinkingReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'thinking';
  });

  const supportReactions: ResourceCommentReactionsViewModel[] = reactions.filter(reaction => {
    return reaction.reaction === 'support';
  });

  /* const reactionIcons: Type[] = [
        { icon: reaction_like },
        { icon: reaction_celebrate },
        { icon: reaction_love },
        { icon: reaction_insightful },
        { icon: reaction_thinking },
        { icon: reaction_support },
    ]; */

  const reactionsHeader: Reaction[] = [
    { count: likeReactions.length, icon: reactionMapData[0].icon },
    { count: celebrateReactions.length, icon: reactionMapData[1].icon },
    { count: loveReactions.length, icon: reactionMapData[2].icon },
    { count: insightfulReactions.length, icon: reactionMapData[3].icon },
    { count: thinkingReactions.length, icon: reactionMapData[4].icon },
    { count: supportReactions.length, icon: reactionMapData[5].icon },
  ];

  const handleButtonClick = (index: number) => {
    setSelectedIndex(index);
  };

  const filteredReactions: ResourceCommentReactionsViewModel[] =
    selectedIndex === 0
      ? reactions
      : selectedIndex === 1
      ? likeReactions
      : selectedIndex === 2
      ? celebrateReactions
      : selectedIndex === 3
      ? loveReactions
      : selectedIndex === 4
      ? insightfulReactions
      : selectedIndex === 5
      ? thinkingReactions
      : selectedIndex === 6
      ? supportReactions
      : [];

  
  return (
    <Transition show={open} as={Fragment}>
      <Dialog className="relative z-10" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="absolute right-0 top-0 hidden sm:block pt-6 pr-6">
                  <button type="button" onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex flex-col items-center gap-3 w-[700px] p-6 rounded-lg bg-white shadow-[3px_3px_2px_0px_rgba(109,108,108,0.25)]">
                  <div className="flex flex-col items-start gap-4 self-stretch">
                    <div className="flex justify-between items-start self-stretch">
                      <p className="text-[36px] font-bold text-[#201047] leading-normal">Reactions</p>
                    </div>
                    <div className="flex  items-center self-stretch relative">
                      <button
                        className={classNames('flex flex-col justify-center items-center w-[80px] rounded-md', selectedIndex === 0 ? 'bg-[rgba(0,78,152,0.05)]' : 'bg-transparent')}
                        onClick={() => handleButtonClick(0)}
                      >
                        <div className="flex h-[43px] p-3 justify-center items-center gap-2 self-stretch">
                          <p className="text-[#004E98] text-[16px] font-medium leading-[20px]">All</p>
                          <p className="text-[#004E98] text-[16px] font-medium leading-[20px]"> {reactions.length} </p>
                        </div>
                        <div className={classNames('h-[2px] self-stretch bg-[#004E98]', selectedIndex === 0 ? 'block' : 'hidden')}></div>
                      </button>

                      {reactionsHeader.map((reaction, index) => (
                        <button
                          key={index}
                          className={classNames(
                            'flex flex-col justify-center items-center w-[80px] rounded-md',
                            selectedIndex === index + 1 ? 'bg-[rgba(0,78,152,0.05)]' : 'bg-transparent'
                          )}
                          onClick={() => handleButtonClick(index + 1)}
                        >
                          <div className="flex h-[43px] p-3 justify-center items-center gap-2 self-stretch">
                            <img src={reaction.icon} alt="like" className="" />
                            <p className="text-[#004E98] text-[16px] font-medium leading-[20px]">{reaction.count}</p>
                          </div>
                          <div className={classNames('h-[2px] self-stretch bg-[#004E98]', selectedIndex === index + 1 ? 'block' : 'hidden')}></div>
                        </button>
                      ))}
                      <div className="absolute bottom-0 left-0 w-[652px] h-[1px] bg-[#EAECF0]"></div>
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-4 self-stretch">
                    {filteredReactions.map((reaction, index) => {
                      const imageIcon = reactionMapData.filter(icons => {
                        if (icons.name.includes(reaction?.reaction)) {
                          return icons;
                        }
                      });

                      return (
                        <div key={index} className="flex w-[560px] items-center gap-3 relative">
                          <div className="flex justify-center items-center  rounded-full bg-[#004E98] w-[40px] h-[40px]">
                            {reaction.usernamePhotoUrl ? (
                              <img src={reaction.usernamePhotoUrl} alt="profile_pic" className="rounded-full w-[40px] h-[40px]" />
                            ) : (
                              <p className="text-white text-[28px] font-normal leading-normal"> {reaction.username[0]} </p>
                            )}
                          </div>

                          <div className="flex flex-col p-1 gap-2 items-start flex-1 rounded-md bg-white">
                            <div className="flex flex-col justify-center items-start gap-0.5">
                              <p className="text-[#201047] text-[16px] font-medium leading-[20px]"> {reaction.username} </p>

                              {reaction?.userBio ? (
                                <p className="overflow-hidden text-[#625B70] text-center text-ellipsis text-[14px] font-medium leading-[20px] capitalize"> {reaction?.userBio} </p>
                              ) : (
                                ''
                              )}
                            </div>
                          </div>

                          <img src={imageIcon[0].icon} alt="like" className="absolute z-10 left-[1.3rem] top-[1.3rem]" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ReactionPopUp;
